import { Box, Grid, Card, Typography, Chip, CircularProgress, Button, Popper, Paper, ClickAwayListener, MenuList, MenuItem, ListSubheader } from "@mui/material";
import Header from "../../../components/Header";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useLocation, useNavigate, useMatch } from "react-router-dom";
import { DataGridPro } from "@mui/x-data-grid-pro";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import API from "../../../api/index";
import IntelligenceTimeFilter from "../../../components/IntelligenceTimeFilter";
import OtSecurityToolBar from "../../../components/OtSecurityToolBar";
import { colors, dataGridStyle } from "../../../theme";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TuneIcon from "@mui/icons-material/Tune";
import { AckButton } from "../../../components/columns";
import { useSettings } from "../../../contexts/SettingsContext";

dayjs.extend(utc);
dayjs.extend(timezone);

// ─── Classification helpers ───────────────────────────────────────────────────

const CLASS_CONFIG = {
    CRITICAL: { color: "#db4f4a", bg: "rgba(219,79,74,0.15)",  icon: <ReportGmailerrorredIcon fontSize="small" /> },
    HIGH:     { color: "#e2724e", bg: "rgba(226,114,78,0.15)", icon: <WarningAmberIcon fontSize="small" /> },
    MEDIUM:   { color: "#f0c050", bg: "rgba(240,192,80,0.15)", icon: <InfoOutlinedIcon fontSize="small" /> },
    LOW:      { color: "#4cceac", bg: "rgba(76,206,172,0.15)", icon: <CheckCircleOutlineIcon fontSize="small" /> },
};

function ClassificationChip({ value }) {
    const upper = value?.toString().toUpperCase();
    const cfg = CLASS_CONFIG[upper] || { color: "#aaa", bg: "rgba(170,170,170,0.1)", icon: null };
    return (
        <Chip
            label={upper || "—"}
            size="small"
            icon={cfg.icon}
            sx={{
                color: cfg.color, borderColor: cfg.color, backgroundColor: cfg.bg,
                fontWeight: 700, fontSize: "0.7rem",
                "& .MuiChip-icon": { color: cfg.color },
            }}
            variant="outlined"
        />
    );
}

// ─── Stats Card ───────────────────────────────────────────────────────────────

function StatCard({ label, value, color, bg, icon, loading }) {
    return (
        <Card sx={{ p: 2, backgroundColor: bg || "rgba(255,255,255,0.04)", borderRadius: 2, minWidth: 120 }}>
            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Box sx={{ color }}>{icon}</Box>
                <Typography variant="body2" sx={{ color: colors.grey[300], fontSize: "0.75rem" }}>{label}</Typography>
            </Box>
            {loading
                ? <CircularProgress size={20} sx={{ color }} />
                : <Typography variant="h4" fontWeight="bold" sx={{ color }}>{value ?? "—"}</Typography>
            }
        </Card>
    );
}

// ─── Static column definitions (no callbacks) ────────────────────────────────

const STATIC_COLUMNS = [
    {
        field: "timestamp", headerName: "Timestamp", width: 175,
        valueFormatter: (value) => value ? dayjs(value).format("YYYY-MM-DD HH:mm:ss") : "—",
    },
    {
        field: "severity", headerName: "Severity", width: 145,
        valueGetter: (value) => value?.toString().toUpperCase() ?? null,
        renderCell: ({ value }) => <ClassificationChip value={value} />,
    },
    { field: "category",   headerName: "Classification",  width: 190 },
    {
        field: "src_ip", headerName: "Source", width: 175,
        renderCell: ({ value, row }) =>
            <span>{value}{row.src_port ? `:${row.src_port}` : ""}</span>,
    },
    {
        field: "dest_ip", headerName: "Destination", width: 175,
        renderCell: ({ value, row }) =>
            <span>{value}{row.dest_port ? `:${row.dest_port}` : ""}</span>,
    },
    {
        field: "signature", headerName: "Details", flex: 1, minWidth: 220,
        renderCell: ({ value }) => (
            <span style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4 }}>{value}</span>
        ),
    },
    { field: "app_proto",  headerName: "Protocol",  width: 100 },
    // { field: "ot_severity",headerName: "OT Sev.",   width: 85,  type: "number" },
    // { field: "count",      headerName: "Count",     width: 80,  type: "number" },
    // { field: "suppressed", headerName: "Suppressed",width: 105, type: "boolean" },
];

// ─── Filter model → API params mapper ────────────────────────────────────────

function buildApiFilters(filterModel) {
    if (!filterModel || filterModel.length === 0) return {};
    return { filters: filterModel };
}

const COL_KEY = "ot";
const DEFAULT_COL_VIS = { ack: false };

// ─── Severity rule config deep-link ───────────────────────────────────────────
// row.signature_id equals the severity_rules catalog's event_type_id — passed through so
// the OT Anomalies tab can auto-select the right notice_type and pre-fill the Add-Rule dialog.
//
// The event data travels via a sessionStorage "mailbox", NOT the URL query string — a URL
// persists (bookmarks, browser history, back/forward, other nav links reusing the current
// search) and kept re-triggering the prefill on plain revisits. sessionStorage is read and
// deleted in the same tick by LecsManagement, so it can only ever fire once per actual click.
function stashAnomalyPrefill(row) {
    if (!row) return;
    const payload = {};
    if (row.signature_id !== undefined && row.signature_id !== null) payload.signature_id = row.signature_id;
    if (row.app_proto) payload.proto = row.app_proto;
    if (row.src_ip) payload.src = row.src_ip;
    if (row.dest_ip) payload.dst = row.dest_ip;
    if (row.severity) payload.severity = row.severity;
    try { sessionStorage.setItem("ot-anomaly-prefill", JSON.stringify(payload)); } catch (_) { console.error("[stashAnomalyPrefill] Failed to write prefill:", _); }
}

// LECS Management doesn't live at the same path in every deployment (on-prem: /lecs-management;
// cloud: /user/lecs-management or /reseller/myC/lecs-management) — basePath is resolved via
// useMatch against those route patterns in the component below, not guessed from the URL string.
function buildAnomalyConfigUrl(basePath) {
    return `${basePath}?tab=2&section=ot_anomalies`;
}

// ─── Main page ────────────────────────────────────────────────────────────────

const OtSecurity = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const isUserCtx = useMatch("/user/*");
    const isResellerCtx = useMatch("/reseller/myC/*");
    const lecsManagementPath = isUserCtx ? "/user/lecs-management" : isResellerCtx ? "/reseller/myC/lecs-management" : "/lecs-management";
    const tz = sessionStorage.getItem("timezone") || "Etc/GMT";
    const abortRef = useRef(null);
    const { settings, updateSettings } = useSettings();
    const [ctxMenu, setCtxMenu] = useState(null); // { mouseX, mouseY, row }

    const [dateFrom, setDateFrom] = useState(() => {
        const q = new URLSearchParams(location.search);
        if (q.get("dateFrom")) return dayjs.tz(q.get("dateFrom"), tz);
        return dayjs().tz(tz).subtract(1, "hour");
    });
    const [dateTo, setDateTo] = useState(() => {
        const q = new URLSearchParams(location.search);
        if (q.get("dateTo")) return dayjs.tz(q.get("dateTo"), tz);
        return dayjs().tz(tz);
    });

    const [filterModel, setFilterModel] = useState([]);
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
    const [loadingRows, setLoadingRows] = useState(false);
    const [stats, setStats] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [hideAcked, setHideAcked] = useState(false);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState(
        settings?.colVis?.[COL_KEY] ?? DEFAULT_COL_VIS
    );

    // Sync column visibility from settings on load
    useEffect(() => {
        if (settings?.colVis?.[COL_KEY]) {
            setColumnVisibilityModel(settings.colVis[COL_KEY]);
        }
    }, [settings]);

    const handleColVisChange = useCallback((model) => {
        setColumnVisibilityModel(model);
        updateSettings("colVis", { ...settings?.colVis, [COL_KEY]: model });
    }, [settings, updateSettings]);

    const handleAck = useCallback(async (row, set) => {
        try {
            await API.setAckEvent({
                event_id: row.event_id,
                set: set ? 1 : 0,
                timestamp: row.timestamp,
                severity: row.severity,
                type: "ot"
            });
            setRows(prev => prev.map(r =>
                r.event_id === row.event_id ? { ...r, acked: set } : r
            ));
        } catch (_) { console.error(_); }
    }, []);

    const columns = useMemo(() => [
        ...STATIC_COLUMNS,
        {
            field: "ack",
            headerName: "ACK",
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => <AckButton row={params.row} onAck={handleAck} />,
        },
    ], [handleAck]);

    const fetchStats = useCallback(async (from, to) => {
        setLoadingStats(true);
        try {
            const res = await API.getOtSecurityStats({
                dateFrom: from.toISOString(),
                dateTo: to.toISOString(),
            });
            setStats(res.data);
        } catch (_) { console.error(_); }
        finally { setLoadingStats(false); }
    }, []);

    const fetchRows = useCallback(async (from, to, page, pageSize, fm, hideAcked) => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoadingRows(true);
        try {
            const res = await API.getOtSecurityEvents(
                {
                    dateFrom: from.toISOString(),
                    dateTo: to.toISOString(),
                    page,
                    pageSize,
                    ...buildApiFilters(fm),
                    hideAcked: hideAcked || false,
                },
                abortRef.current.signal
            );
            const data = res.data;
            setRows(data.rows.map((r) => ({ ...r, id: r._id || r.event_id || String(Math.random()) })));
            setRowCount(data.total);
        } catch (_) { console.error(_); }
        finally { setLoadingRows(false); }
    }, []);

    // Re-fetch when date range changes (reset to page 0)
    useEffect(() => {
        if (!dateFrom || !dateTo) return;
        setPaginationModel((p) => ({ ...p, page: 0 }));
        fetchStats(dateFrom, dateTo);
        fetchRows(dateFrom, dateTo, 0, paginationModel.pageSize, filterModel, hideAcked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFrom, dateTo]);

    // Re-fetch when filters or hideAcked changes (reset to page 0)
    useEffect(() => {
        if (!dateFrom || !dateTo) return;
        setPaginationModel((p) => ({ ...p, page: 0 }));
        fetchRows(dateFrom, dateTo, 0, paginationModel.pageSize, filterModel, hideAcked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterModel, hideAcked]);

    // Re-fetch when page/pageSize changes
    useEffect(() => {
        if (!dateFrom || !dateTo) return;
        fetchRows(dateFrom, dateTo, paginationModel.page, paginationModel.pageSize, filterModel, hideAcked);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel.page, paginationModel.pageSize]);

    const handleDateChange = useCallback(({ dateFrom: f, dateTo: t }) => {
        setDateFrom(dayjs(f).tz(tz));
        setDateTo(dayjs(t).tz(tz));
    }, [tz]);

    const classCount = (cls) => stats?.bySeverity?.[cls.toLowerCase()] ?? 0;

    return (
        <Box m="20px">
            <Grid container spacing={2}>
                <Grid size={12} sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                    <Header
                        title="OT Security"
                        subtitle=""
                        desc="Security events detected on the OT network classified by the Specto engine (CRITICAL, HIGH, MEDIUM, LOW). Use FILTER to narrow results; EXPORT to download CSV."
                    />
                    <Button
                        variant="outlined" size="small" startIcon={<TuneIcon fontSize="small" />}
                        onClick={() => navigate(buildAnomalyConfigUrl(lecsManagementPath))}
                        sx={{ flexShrink: 0, mt: 1, color: "#449095", borderColor: "rgba(69,144,149,0.4)", "&:hover": { borderColor: "#449095", backgroundColor: "rgba(69,144,149,0.08)" } }}
                    >
                        Configure Severity Rules
                    </Button>
                </Grid>

                <Grid size={12}>
                    <IntelligenceTimeFilter
                        onDateChange={handleDateChange}
                        initialDateFrom={dateFrom}
                        initialDateTo={dateTo}
                        title="Time Range"
                        compact
                    />
                </Grid>

                {/* Stats cards */}
                <Grid size={12}>
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <StatCard label="Total Events" value={stats?.total} color={colors.blueAccent[300]}
                            icon={<PrecisionManufacturingIcon />} loading={loadingStats} />
                        <StatCard label="Critical" value={classCount("CRITICAL")}
                            color={CLASS_CONFIG.CRITICAL.color} bg={CLASS_CONFIG.CRITICAL.bg}
                            icon={<ReportGmailerrorredIcon />} loading={loadingStats} />
                        <StatCard label="High" value={classCount("HIGH")}
                            color={CLASS_CONFIG.HIGH.color} bg={CLASS_CONFIG.HIGH.bg}
                            icon={<WarningAmberIcon />} loading={loadingStats} />
                        <StatCard label="Medium" value={classCount("MEDIUM")}
                            color={CLASS_CONFIG.MEDIUM.color} bg={CLASS_CONFIG.MEDIUM.bg}
                            icon={<InfoOutlinedIcon />} loading={loadingStats} />
                        <StatCard label="Low" value={classCount("LOW")}
                            color={CLASS_CONFIG.LOW.color} bg={CLASS_CONFIG.LOW.bg}
                            icon={<CheckCircleOutlineIcon />} loading={loadingStats} />
                    </Box>
                </Grid>

                {/* DataGrid */}
                <Grid size={12}>
                    <Box sx={{ height: "65vh", ...dataGridStyle }}>
                        <DataGridPro
                            rows={rows}
                            columns={columns}
                            loading={loadingRows}
                            rowCount={rowCount}
                            pagination
                            paginationMode="server"
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            pageSizeOptions={[25, 50, 100, 200]}
                            density="compact"
                            disableRowSelectionOnClick
                            showToolbar
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={handleColVisChange}
                            getRowHeight={() => "auto"}
                            getEstimatedRowHeight={() => 52}
                            sx={{ "& .MuiDataGrid-cell": { py: "5px" } }}
                            slots={{ toolbar: OtSecurityToolBar }}
                            slotProps={{
                                toolbar: { filterModel, setFilterModel, hideAcked, setHideAcked },
                                row: {
                                    onContextMenu: (event) => {
                                        const id = event.currentTarget.getAttribute("data-id");
                                        const row = rows.find((r) => r.id === id);
                                        event.preventDefault();
                                        setCtxMenu({ mouseX: event.clientX, mouseY: event.clientY, row });
                                    },
                                    style: { cursor: "context-menu" },
                                },
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>

            {/* A MUI Menu renders a modal Backdrop that covers the whole viewport (even though
                invisible) — clicking the app's main nav while this was open would only dismiss
                the backdrop, swallowing that click instead of also navigating, forcing a second
                click. Popper + ClickAwayListener has no such overlay, so a click on the nav both
                closes this menu AND still reaches the nav link underneath, in one click. */}
            <Popper
                open={!!ctxMenu}
                anchorEl={ctxMenu ? {
                    getBoundingClientRect: () => ({
                        top: ctxMenu.mouseY, left: ctxMenu.mouseX, right: ctxMenu.mouseX, bottom: ctxMenu.mouseY,
                        width: 0, height: 0, x: ctxMenu.mouseX, y: ctxMenu.mouseY, toJSON: () => {},
                    }),
                } : null}
                placement="bottom-start"
                sx={{ zIndex: (theme) => theme.zIndex.modal }}
            >
                <ClickAwayListener onClickAway={() => setCtxMenu(null)}>
                    <Paper sx={{ minWidth: 240, backgroundColor: "#393939", color: "#eaeaea" }} onKeyDown={(e) => { if (e.key === "Escape") setCtxMenu(null); }}>
                        <MenuList sx={{ py: 0 }} autoFocusItem={!!ctxMenu}>
                            <ListSubheader sx={{ backgroundColor: "#2a2a2a", color: "#eaeaea", lineHeight: "40px" }}>Severity Rules</ListSubheader>
                            <MenuItem onClick={() => { stashAnomalyPrefill(ctxMenu?.row); navigate(buildAnomalyConfigUrl(lecsManagementPath)); setCtxMenu(null); }}>
                                <TuneIcon fontSize="small" sx={{ mr: 1.5 }} /> Configure Severity for This Event
                            </MenuItem>
                        </MenuList>
                    </Paper>
                </ClickAwayListener>
            </Popper>
        </Box>
    );
};

export default OtSecurity;
